import { PadLock } from "@/components/Icons";
import IngredientCheckButton from "@/components/IngredientCheckButton";
import Nutrition from "@/components/Nutrition";
import ScaledIngredientLabel from "@/components/ScaledIngredientLabel";
import ScaledIngredientQuantity from "@/components/ScaledIngredientQuantity";
import { GeoPreferenceContext } from "@/contexts/GeoPreferenceContext";
import usePayWall from "@/hooks/usePaywall";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import { pushEvent } from "@/lib/gtm";
import normalizeRecipeIngredients from "@/lib/ingredients/normalizeRecipeIngredients";
import RedactedOverlay from "@components/RedactedOverlay";
import { Tab } from "@headlessui/react";
import clsx from "clsx";
import { motion } from "framer-motion";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { RotateCcw } from "react-feather";
import { useLocalStorage } from "react-use";
import MeasurementSelector from "./MeasurementSelector";
import ServingSizeControl from "./ServingSizeControl";
import Spinner from "./Spinner";

const Ingredients = observer(({ recipe, redacted, isLoading }) => {
  const { measurement } = React.useContext(GeoPreferenceContext);

  const [checkedIngredients, setCheckedIngredients] = useLocalStorage(
    `checkedIngredients-${recipe.id}`,
    []
  );

  const { subscriptionPermission } = useSubscriptionPermission(recipe);

  const ingredients = normalizeRecipeIngredients(
    recipe.recipeIngredients,
    recipe.servingSize,
    false,
    measurement
  );

  const ref = useRef(null);

  const { setPaywall } = usePayWall();

  const [tab, setTab] = useState(0);
  const [servingSize, setServingSize] = useState(parseInt(recipe.servingSize));

  const isScaled = parseInt(`${servingSize}`) !== parseInt(recipe.servingSize);

  return (
    <div className="Ingredients @container/ingredients" ref={ref}>
      <Tab.Group
        onChange={(tab) => {
          if (tab === 1) {
            if (!subscriptionPermission.canUseNutritionalInfo) {
              setPaywall({
                isOpen: true,
                isClosable: true,
                title: recipe.title,
                variant: "nutrition",
              });

              return;
            }
          }
          setTab(tab);
        }}
        selectedIndex={tab}
      >
        <div
          className={clsx(
            "gap-4md:@[410px]:flex-row flex items-center justify-start lg:justify-between lg:pb-8"
          )}
        >
          <Tab.List
            className={clsx("tab-list-sm hidden lg:inline-flex print:!hidden")}
          >
            <Tab key="ingredients" className={clsx("tab")}>
              Ingredients
            </Tab>
            <Tab key="nutrition" className={clsx("tab")}>
              Nutrition
            </Tab>
          </Tab.List>
        </div>
        <div className="flex flex-col items-center space-y-2 sm:flex-row sm:items-center sm:justify-center md:items-center md:justify-center lg:justify-between">
          {tab === 0 && (
            <_ServingControl
              recipe={recipe}
              isScaled={isScaled}
              servingSize={servingSize}
              setServingSize={setServingSize}
            />
          )}
        </div>
        <Tab.Panels>
          <Tab.Panel key="ingredients">
            {isScaled && (
              <_ScaledIngredients
                recipe={recipe}
                ingredients={ingredients}
                checkedIngredients={checkedIngredients}
                setCheckedIngredients={setCheckedIngredients}
                servingSize={servingSize}
                redacted={redacted}
                isLoading={isLoading}
              />
            )}
            {!isScaled && (
              <_BaseIngredients
                recipe={recipe}
                ingredients={ingredients}
                checkedIngredients={checkedIngredients}
                setCheckedIngredients={setCheckedIngredients}
                servingSize={servingSize}
                redacted={redacted}
                isLoading={isLoading}
              />
            )}
          </Tab.Panel>
          <Tab.Panel key="nutrition">
            <Nutrition recipe={recipe} />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
});

const _ServingControl = ({
  recipe,
  isScaled,
  servingSize,
  setServingSize,
  theme = "light" as "light" | "dark",
}) => {
  const { subscriptionPermission } = useSubscriptionPermission(recipe);
  const { setPaywall } = usePayWall();

  const scalePaywall = {
    isOpen: true,
    isClosable: true,
    variant: "changeScale",
    title: recipe.title,
  };

  const handleServingSizeChange = (newServingSize) => {
    if (subscriptionPermission.canUseRecipeScaling) {
      setServingSize(newServingSize);
    } else {
      setPaywall(scalePaywall);
    }
  };

  return (
    <>
      <div className={clsx("flex items-center space-x-4 print:hidden")}>
        <div className="flex items-center space-x-1">
          <ServingSizeControl
            servingSize={servingSize}
            onServingSizeChange={handleServingSizeChange}
            size="lg"
            theme="dark"
          />
          <motion.button
            disabled={!isScaled}
            className={`animate-spin-once flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 bg-zinc-100 text-zinc-800 opacity-100 transition duration-300 disabled:animate-none  disabled:opacity-20 `}
            onClick={(e) => {
              setServingSize(parseInt(recipe.servingSize));
              e.preventDefault();
            }}
            // Spin the button when parseInt(servingSize) !== parseInt(recipe.servingSize)
            animate={{
              rotate: isScaled ? 360 : 0,
            }}
            transition={{
              duration: isScaled ? 0 : 0.3,
            }}
          >
            <RotateCcw className={clsx("h-4 w-4 stroke-[1.5]")} />
          </motion.button>
        </div>
        <div className="border-l pl-4">
          <MeasurementSelector />
        </div>
      </div>
    </>
  );
};

const _BaseIngredients = ({
  recipe,
  ingredients,
  checkedIngredients,
  setCheckedIngredients,
  servingSize,
  redacted,
  isLoading,
  theme = "light" as "light" | "dark",
}) => {
  return (
    <div
      className={clsx("pt-6", {
        "relative max-h-[325px] overflow-hidden lg:max-h-[450px]": redacted,
      })}
    >
      {redacted && <RedactedOverlay />}
      <div className="flex flex-col">
        {ingredients.map((ingredient) => {
          const isChecked = checkedIngredients.includes(ingredient.uniqueKey);

          return (
            <_Ingredient
              key={ingredient.originalId}
              ingredient={ingredient}
              isChecked={isChecked}
              checkedIngredients={checkedIngredients}
              setCheckedIngredients={setCheckedIngredients}
              servingSize={servingSize}
              isScaled={false}
              redacted={redacted}
              isLoading={isLoading}
            />
          );
        })}
      </div>
    </div>
  );
};

const _ScaledIngredients = ({
  ingredients,
  checkedIngredients,
  setCheckedIngredients,
  servingSize,
  redacted,
  isLoading,
  recipe,
}) => {
  return (
    <div className="pt-10 md:py-6 print:pt-0">
      <div className={clsx("mb-6 space-y-2")}>
        <p className="font-body text-sm">
          This recipe was created for a specific serving size. If you want to
          scale it up or down you might need to make adjustments to the cooking
          time, seasoning quantities, and equipment used.
        </p>
      </div>
      <div className="flex flex-col">
        {ingredients.map((ingredient) => {
          const isChecked = checkedIngredients.includes(ingredient.uniqueKey);

          return (
            <_Ingredient
              key={ingredient.uniqueKey}
              ingredient={ingredient}
              isChecked={isChecked}
              checkedIngredients={checkedIngredients}
              setCheckedIngredients={setCheckedIngredients}
              servingSize={servingSize}
              redacted={redacted}
              isLoading={isLoading}
            />
          );
        })}
      </div>
    </div>
  );
};

const _Ingredient = ({
  ingredient,
  isChecked,
  checkedIngredients,
  setCheckedIngredients,
  servingSize = 1,
  redacted,
  isLoading,
}) => {
  const toggleIngredientCheck = () => {
    if (checkedIngredients.includes(ingredient.uniqueKey)) {
      setCheckedIngredients(
        checkedIngredients.filter((key) => key !== ingredient.uniqueKey)
      );
    } else {
      const newCheckedIngredients = [
        ...checkedIngredients,
        ingredient.uniqueKey,
      ];
      setCheckedIngredients(newCheckedIngredients);
    }
  };

  if (ingredient.type === "heading") {
    return (
      <div className={clsx("pt-4 text-base capitalize")}>
        {ingredient.heading}
      </div>
    );
  }

  const forceHideScaledOnTheseIngredients = [
    "Salt",
    "Olive Oil",
    "Pepper",
    "Black Pepper",
    "Vegetable Oil",
  ];

  return (
    <>
      <div
        className={clsx(
          "Ingredients__ingredient",
          "group flex w-full items-center py-2 text-left focus:border-zinc-200 lg:py-3 print:py-1 [&:not(:last-child)]:border-b"
        )}
      >
        <div
          className={clsx("flex grow items-center", {
            "opacity-25": isChecked,
          })}
        >
          <>
            <div className={clsx("w-2/6 text-sm lg:text-base")}>
              {redacted ? (
                <PadLock size={24} />
              ) : isLoading ? (
                <Spinner />
              ) : (
                <>
                  {/* If ingredient.ingredient is in forceHideScaledOnTheseIngredients and it's been converted (eg: no value was set by an editor in the cms) */}
                  {forceHideScaledOnTheseIngredients.includes(
                    ingredient.ingredient
                  ) && ingredient.hasBeenConverted ? (
                    <></>
                  ) : (
                    <ScaledIngredientQuantity
                      ingredient={ingredient}
                      scale={servingSize}
                    />
                  )}
                </>
              )}
            </div>

            <div className={clsx("w-4/6 font-body text-base 2xl:text-lg")}>
              <ScaledIngredientLabel
                ingredient={ingredient}
                scale={servingSize}
              />
            </div>
          </>
        </div>
        <IngredientCheckButton
          isChecked={isChecked}
          onClick={toggleIngredientCheck}
        />
      </div>
    </>
  );
};

export default Ingredients;
